/**
 * Configurações → Financeiro → Categorias de financiamento (rodada
 * LUCRO-OPERACIONAL 2026-07-17). Lançamentos destas categorias (empréstimos,
 * antecipações…) ficam FORA do lucro (Receitas/Despesas/Resultado/Margem da
 * Visão Geral, e-mails, meta de resultado) mas DENTRO do caixa/saldo/fluxo.
 * Tudo no servidor (SQLite) — ZERO localStorage.
 */
import { useEffect, useState } from 'react';

import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Alert, Button } from '@/components/ui';

interface CategoriaItem {
  empresaId: string;
  codigo: string;
  descricao: string | null;
  financiamento: 0 | 1;
}

const EMPRESA_ROTULO: Record<string, string> = {
  'empresa-igm-ag': 'AG DOS SANTOS',
  'empresa-igm-mussi': 'MATHEUS MUSSI',
};
const rotuloEmpresa = (id: string) => EMPRESA_ROTULO[id] ?? id;

export function FinanceiroFinanciamentoConfigCard() {
  const [items, setItems] = useState<CategoriaItem[]>([]);
  const [selecao, setSelecao] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    setErr(null);
    Promise.resolve(
      api.get<{ items: CategoriaItem[] }>('/api/v1/admin/financeiro/financiamento-config'),
    )
      .then((r) => setItems(r?.items ?? []))
      .catch((e: Error) => setErr(e.message));
  }, [tentativa]);

  const marcadas = items.filter((i) => i.financiamento === 1);
  const disponiveis = items.filter((i) => i.financiamento === 0);

  async function alternar(item: CategoriaItem, financiamento: boolean) {
    setSalvando(true);
    setErr(null);
    try {
      await api.post('/api/v1/admin/financeiro/financiamento-config', {
        empresaId: item.empresaId,
        categoriaCodigo: item.codigo,
        financiamento,
      });
      setItems((prev) =>
        prev.map((i) =>
          i.empresaId === item.empresaId && i.codigo === item.codigo
            ? { ...i, financiamento: financiamento ? 1 : 0 }
            : i,
        ),
      );
      setSelecao('');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card className="mt-6" data-testid="financeiro-financiamento-config">
      <CardHeader>
        <CardTitle>Categorias de financiamento</CardTitle>
        <CardDescription>
          Lançamentos destas categorias (empréstimos, antecipações de recebíveis…) <strong>não
          contam como receita ou despesa</strong> nos números de lucro da Visão Geral e dos
          relatórios — mas <strong>continuam no caixa e no saldo</strong> (o dinheiro entrou ou
          saiu de verdade). Eles aparecem à parte no bloco &quot;Financiamento no mês&quot;.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {err && (
          <Alert variant="danger" className="mb-3">
            {err}{' '}
            <button className="underline" onClick={() => setTentativa((t) => t + 1)}>
              Tentar de novo
            </button>
          </Alert>
        )}
        {marcadas.length === 0 ? (
          <div className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
            Nenhuma categoria marcada como financiamento.
          </div>
        ) : (
          <div className="space-y-1.5 mb-4">
            {marcadas.map((i) => (
              <div
                key={`${i.empresaId}:${i.codigo}`}
                className="flex items-center justify-between gap-3 border border-line dark:border-line-dark rounded-lg px-3 py-2"
              >
                <span className="text-sm text-ink-1 dark:text-ink-dark-1 min-w-0 truncate">
                  {i.descricao ?? i.codigo}
                  <span className="text-xs text-ink-3 dark:text-ink-dark-3 ml-2">
                    {rotuloEmpresa(i.empresaId)}
                  </span>
                </span>
                <button
                  onClick={() => alternar(i, false)}
                  disabled={salvando}
                  aria-label={`Remover ${i.descricao ?? i.codigo} do financiamento`}
                  className="text-xs text-red hover:underline min-h-[44px] px-2 shrink-0"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Gate FASE-C: os rótulos longos das categorias do Omie davam largura
            intrínseca de 394px ao select — em 375px a tela inteira estourava
            59px. `w-full` + `min-w-0` prendem o campo na coluna. */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap min-w-0">
          <label className="text-xs text-ink-2 dark:text-ink-dark-2 min-w-0 w-full sm:w-auto">
            Adicionar categoria
            <select
              value={selecao}
              onChange={(e) => setSelecao(e.target.value)}
              data-testid="financiamento-adicionar"
              className="block mt-1 w-full min-w-0 max-w-full bg-paper dark:bg-paper-dark border border-line dark:border-line-dark rounded-lg px-2 py-2 text-sm text-ink-1 dark:text-ink-dark-1"
            >
              <option value="">Escolher…</option>
              {disponiveis.map((i) => (
                <option key={`${i.empresaId}:${i.codigo}`} value={`${i.empresaId}:${i.codigo}`}>
                  {(i.descricao ?? i.codigo) + ' — ' + rotuloEmpresa(i.empresaId)}
                </option>
              ))}
            </select>
          </label>
          <Button
            onClick={() => {
              const [empresaId, codigo] = selecao.split(':');
              const item = items.find((i) => i.empresaId === empresaId && i.codigo === codigo);
              if (item) alternar(item, true);
            }}
            disabled={salvando || !selecao}
            data-testid="financiamento-marcar"
          >
            {salvando ? 'Salvando…' : 'Marcar como financiamento'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
